import { useCallback, useState, useEffect, useRef, useMemo } from "react";
import { toast } from "react-toastify";
import { apiClient } from "../../api/httpClient";
import { buildBondStatusPatchPayload } from "../../globals/utils/bondStatusPatchUtils";
import { getBondUpdateEndpoint } from "./bondStatusUpdateHelpers";
import { useBondEndorsementDialogState } from "./useBondEndorsementDialogState";
import {
  DWB_LABEL_MAP,
  isNameChangeEndorsement,
} from "./bondsEndorsementSharedConfig";
import {
  getCancellationInstructionsList,
  getEndorsementInstructions,
} from "../Law Firm User/helpers/lawFirmBondsCancellationCopy";
export {
  formatCorrectionFieldLabel,
  formatCorrectionFromTo,
} from "../../features/bonds/shared/bondCorrectionHelpers";
export { isNameChangeEndorsement, DWB_LABEL_MAP };

export const bondTypes = ["Probate", "Guardianship", "Blanket", "VA", "Trustee"];

export const bondTabs = [
  "Active",
  "Approved",
  "Rejected",
  "To Be Cancelled",
  "Data Corrections",
];

export const getUnderwritingChip = (statusRaw) => {
  const status = (statusRaw || "").toString().trim().toLowerCase();
  if (status === "pending") return { label: "Pending", color: "warning" };
  if (status === "approved") return { label: "Approved", color: "success" };
  if (status === "not required") return { label: "Not Required", color: "default" };
  if (status === "overrided") return { label: "Not Required", color: "default" };
  if (status) return { label: status.charAt(0).toUpperCase() + status.slice(1), color: "default" };
  return { label: "-", color: "default" };
};

export const getBondRetrieveUrl = (bondType, bondId) => {
  if (!bondId) return null;
  const type = (bondType || "").toString().trim();
  if (type === "VA") return `/vermost-va-bond-update/${bondId}`;
  if (type === "Trustee") return `/vermost-trustee-bond-update/${bondId}`;
  if (type === "Probate") return `/vermost-probate-bond-update/${bondId}`;
  if (type === "Blanket") return `/vermost-blanket-bond-update/${bondId}`;
  if (type === "Guardianship") return `/vermost-guardianship-bond-update/${bondId}`;
  return `/vermost-guardianship-bond-update/${bondId}`;
};

/** Archive API excludes Rejected; Approve Bonds Rejected tab uses list-bonds (A25). */
export const REJECTED_LIST_BONDS_QUERY =
  "/list-bonds/?status=Rejected&start=0&end=10000";

export const mapListBondRow = (b) => {
  const principalOne = b.principal_one || "";
  return {
    bond_id: b.bond_id,
    bond_number: b.bond_number || "",
    bond_type: b.bond_type,
    bond_sub_category: b.bond_sub_category,
    name: b.name,
    status: b.status || "Rejected",
    underwriting_status: b.underwriting_status ?? "-",
    law_firm_name: b.law_firm_name,
    law_firm_user_name: b.law_firm_user_name,
    bond_amount: b.bond_amount ?? "0.00",
    principals: principalOne ? [principalOne] : [],
    principal_one: principalOne,
    referral_source: b.referral_source,
    referred_by: b.referred_by,
    last_endorsement_at: b.last_endorsement_at,
    created_at: b.created_at,
  };
};

function mergeApproveBondsCacheRows(archiveRows, rejectedRows) {
  const byId = new Map();
  for (const row of archiveRows) {
    if (row.bond_id != null) {
      byId.set(row.bond_id, row);
    }
  }
  for (const row of rejectedRows) {
    if (row.bond_id != null && !byId.has(row.bond_id)) {
      byId.set(row.bond_id, row);
    }
  }
  return [...byId.values()];
}

export default function useApproveBonds() {
  const [selectedType, setselectedType] = useState("Probate"); // Default to Probate
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [bonds, setBonds] = useState([]);
  const [toBeCancelledBonds, setToBeCancelledBonds] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [activeCounts, setActiveCounts] = useState({});
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(50);
  const [hasMore, setHasMore] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const {
    role,
    endorsementDialogOpen,
    setEndorsementDialogOpen,
    selectedBondToEndorse,
    setSelectedBondToEndorse,
    endorsementType,
    setEndorsementType,
    newBondAmount,
    setNewBondAmount,
    endorsementFile,
    setEndorsementFile,
    isSubmittingEndorsement,
    setIsSubmittingEndorsement,
    contactName,
    setContactName,
    contactEmail,
    setContactEmail,
    updatedName,
    setUpdatedName,
    counties,
    endorsementToCounty,
    setEndorsementToCounty,
    loadingCounties,
    estimatedAssets,
    setEstimatedAssets,
    dwbFirstName,
    setDwbFirstName,
    dwbMiddleName,
    setDwbMiddleName,
    dwbLastName,
    setDwbLastName,
    dwbSuffix,
    setDwbSuffix,
    dwbAlias,
    setDwbAlias,
    endorsementPrincipalId,
    setEndorsementPrincipalId,
  } = useBondEndorsementDialogState();

  const [selectedBondToCancel, setSelectedBondToCancel] = useState(null); // State for selected bond to cancel
  const [cancellationFile, setCancellationFile] = useState(null); // State for cancellation file
  const [isMarkingCancellationComplete, setIsMarkingCancellationComplete] = useState(false);
  const [dataCorrectionsList, setDataCorrectionsList] = useState([]);
  const [dataCorrectionsLoading, setDataCorrectionsLoading] = useState(false);
  const dataCorrectionsFetched = useRef(false);
  const [selectedDataCorrection, setSelectedDataCorrection] = useState(null);
  const [dataCorrectionDetailOpen, setDataCorrectionDetailOpen] = useState(false);
  const [completingCorrectionId, setCompletingCorrectionId] = useState(null);
  const isFirstRender = useRef(true);
  const approveBondsArchiveCacheRef = useRef(null);
  // NOTE: count/navigate were previously used, but are not needed on this page currently.

  const mapArchiveBondRow = (b) => {
    const principals = Array.isArray(b.principals) ? b.principals : [];
    return {
      bond_id: b.bond_id,
      bond_number: b.bond_number || "",
      bond_type: b.bond_type,
      bond_sub_category: b.bond_sub_category,
      name: b.name,
      status: b.status || "Active",
      underwriting_status: b.underwriting_status ?? "-",
      law_firm_name: b.law_firm_name,
      law_firm_user_name: b.law_firm_user_name,
      bond_amount: b.bond_amount || "0.00",
      principals,
      principal_one: principals[0] || "",
      referral_source: b.referral_source,
      referred_by: b.referred_by,
      last_endorsement_at: b.last_endorsement_at,
      created_at: b.created_at,
    };
  };

  const fetchToBeCancelledBonds = useCallback(async () => {
    try {
      const raw = await apiClient("/bonds-to-be-cancelled", "GET");
      setToBeCancelledBonds(Array.isArray(raw) ? raw : []);
    } catch (e) {
      console.error("Failed to load bonds to be cancelled:", e);
      setToBeCancelledBonds([]);
    }
  }, []);

  const fetchBonds = useCallback(async (startIdx, endIdx) => {
    setLoading(true);
    try {
      let all = approveBondsArchiveCacheRef.current;
      if (startIdx === 0 || !all) {
        const [archiveResult, rejectedResult] = await Promise.allSettled([
          apiClient("/archive-bonds", "GET"),
          apiClient(REJECTED_LIST_BONDS_QUERY, "GET"),
        ]);

        if (archiveResult.status === "rejected") {
          throw archiveResult.reason;
        }

        const archiveRows = (Array.isArray(archiveResult.value) ? archiveResult.value : []).map(
          mapArchiveBondRow
        );

        let rejectedRows = [];
        if (rejectedResult.status === "fulfilled") {
          rejectedRows = (Array.isArray(rejectedResult.value) ? rejectedResult.value : []).map(
            mapListBondRow
          );
        } else {
          console.error("Failed to load rejected bonds:", rejectedResult.reason);
        }

        all = mergeApproveBondsCacheRows(archiveRows, rejectedRows);
        approveBondsArchiveCacheRef.current = all;
      }
      const page = all.slice(startIdx, endIdx);
      if (startIdx === 0) {
        setBonds(page);
      } else {
        setBonds((prev) => [...prev, ...page]);
      }
      setHasMore(endIdx < all.length);
      setStart(endIdx);
      setEnd(endIdx + Math.max(endIdx - startIdx, 1));
    } catch (e) {
      toast.error(e?.message || "Failed to load bonds");
      if (startIdx === 0) {
        setBonds([]);
        approveBondsArchiveCacheRef.current = null;
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      fetchBonds(0, 50);
      fetchToBeCancelledBonds();
    }
  }, [fetchBonds, fetchToBeCancelledBonds]);

  // Fetch all bond data corrections once when page mounts
  useEffect(() => {
    if (dataCorrectionsFetched.current) return;
    dataCorrectionsFetched.current = true;
    setDataCorrectionsLoading(true);
    apiClient("/bond-data-corrections/", "GET")
      .then((res) => setDataCorrectionsList(Array.isArray(res) ? res : []))
      .catch((e) => {
        console.error("Failed to fetch bond data corrections:", e);
        setDataCorrectionsList([]);
      })
      .finally(() => setDataCorrectionsLoading(false));
  }, []);

  const getCorrectionId = (row) => row?.correction_id ?? row?.id;
  const updateCorrectionStatusToComplete = async (row) => {
    const correctionId = getCorrectionId(row);
    if (correctionId == null) {
      toast.error("Cannot update: correction ID missing");
      return;
    }
    setCompletingCorrectionId(correctionId);
    try {
      await apiClient(`/bond-data-corrections/${correctionId}/status/`, "PATCH", { status: "Complete" });
      toast.success("Status updated to Complete");
      setDataCorrectionsList((prev) =>
        prev.map((r) => (getCorrectionId(r) === correctionId ? { ...r, status: "Complete" } : r))
      );
      if (selectedDataCorrection && getCorrectionId(selectedDataCorrection) === correctionId) {
        setSelectedDataCorrection((prev) => (prev ? { ...prev, status: "Complete" } : null));
      }
    } catch (e) {
      toast.error(e?.message || "Failed to update status");
    } finally {
      setCompletingCorrectionId(null);
    }
  };

  const refreshData = () => {
    approveBondsArchiveCacheRef.current = null;
    setBonds([]);
    setToBeCancelledBonds([]);
    fetchBonds(0, 50);
    fetchToBeCancelledBonds();
    setStart(0);
    setEnd(50);
    setPage(0);
  };

  useEffect(() => {
    // Reset tabIndex if it's out of bounds
    if (tabIndex >= bondTabs.length) {
      setTabIndex(0);
    }
  }, [tabIndex]);

  const calculateStatusCounts = useCallback((bondsData, bondType) => {
    if (!bondType) return;

    const counts = bondTabs.reduce((acc, status) => {
      if (status.toLowerCase() === "to be cancelled") {
        // Count "To Be Cancelled" bonds by selected type
        acc[status.toLowerCase()] = toBeCancelledBonds.filter(
          (bond) => !bondType || bond.bond_type === bondType
        ).length;
      } else {
        // Count from regular bonds data
        acc[status.toLowerCase()] = bondsData.filter(
          (bond) =>
            bond.bond_type === bondType && bond.status.toLowerCase() === status.toLowerCase()
        ).length;
      }
      return acc;
    }, {});
    setStatusCounts(counts);
  }, [toBeCancelledBonds]);

  const calculateActiveCounts = useCallback((bondsData) => {
    const counts = bondTypes.reduce((acc, type) => {
      const activeCount = bondsData.filter(
        (bond) => bond.bond_type === type && bond.status.toLowerCase() === "active"
      ).length;
      const toBeCancelledCount = toBeCancelledBonds.filter(
        (bond) => bond.bond_type === type
      ).length;
      acc[type] = activeCount + toBeCancelledCount;
      return acc;
    }, {});
    setActiveCounts(counts);
  }, [toBeCancelledBonds]);

  useEffect(() => {
    calculateStatusCounts(bonds, selectedType);
    calculateActiveCounts(bonds);
  }, [bonds, selectedType, calculateStatusCounts, calculateActiveCounts]);


  const formatBondAmount = (amount) => {
    if (amount === null) return "$0.00";
    return `$${Number(amount).toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  };

  const filteredData = useMemo(() => {
    const statusFilter = bondTabs[tabIndex]?.toLowerCase() || "active";

    if (statusFilter === "to be cancelled") {
      console.log("Selected Type:", selectedType);
      console.log("Search Query:", searchQuery);
      // Use the special API data for "To Be Cancelled" bonds
      const result = toBeCancelledBonds.filter((bond) => {
        const bondTypeMatch = !selectedType || bond.bond_type === selectedType;
        const principalsStr = Array.isArray(bond.principals)
          ? bond.principals.map(String).join(" ").toLowerCase()
          : String(bond.principals ?? "").toLowerCase();
        const principalMatch = principalsStr.includes(searchQuery.toLowerCase())
          || bond.name?.toLowerCase().includes(searchQuery.toLowerCase()) || false;
        const lawFirmMatch = bond.law_firm_name?.toLowerCase().includes(searchQuery.toLowerCase()) || false;
        const bondNumberMatch = bond.bond_number?.toLowerCase().includes(searchQuery.toLowerCase()) || false;
        const lawFirmUserMatch = bond.law_firm_user_name?.toLowerCase().includes(searchQuery.toLowerCase()) || false;
        return bondTypeMatch && (principalMatch || lawFirmMatch || bondNumberMatch || lawFirmUserMatch);
      });
      console.log("Filtered To Be Cancelled Bonds:", result);
      return result;
    }

    // Use regular bonds data for other tabs
    return bonds.filter((bond) => {
      const bondTypeMatch = selectedType ? bond.bond_type === selectedType : true;
      const statusMatch = bond.status.toLowerCase() === statusFilter;
      const principalMatch =
        bond.principal_one?.toLowerCase().includes(searchQuery.toLowerCase()) ??
        false;

      return bondTypeMatch && statusMatch && principalMatch;
    });
  }, [tabIndex, selectedType, searchQuery, toBeCancelledBonds, bonds]);

  const currentRows = useMemo(() => {
    return bondTabs[tabIndex]?.toLowerCase() === "to be cancelled"
      ? filteredData  // Show all cancelled bonds without pagination
      : filteredData.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      );
  }, [tabIndex, filteredData, page, rowsPerPage]);

  const handleBondTypeClick = (bondType) => {
    setselectedType(bondType);
    setTabIndex(0);
    setPage(0);
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
    // Reset page when switching tabs to avoid pagination issues
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const [stats, setStats] = useState({
    totalApprovedBonds: 0,
    averageProcessingTime: 0,
  });
  const [dataLoading, setDataLoading] = useState(true);
  const [statsOpen, setStatsOpen] = useState(false); // State to control dialog open/close

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await apiClient("/average-processing-time/");
        setStats({
          totalApprovedBonds: data.total_approved_bonds || 0,
          averageProcessingTime: data.average_processing_time_minutes || 0,
        });
      } catch (error) {
        console.error("Failed to fetch processing time:", error);
      } finally {
        setDataLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  const handleMarkCancellationComplete = async () => {
    if (!selectedBondToCancel) return;

    setIsMarkingCancellationComplete(true);
    try {
      const endpoint = getBondUpdateEndpoint(
        selectedBondToCancel.bond_type,
        selectedBondToCancel.bond_id
      );
      if (!endpoint) throw new Error("Invalid bond type");

      const payload = buildBondStatusPatchPayload(
        selectedBondToCancel.bond_type,
        "Cancelled"
      );

      await apiClient(endpoint, "PATCH", payload);
      toast.success("Bond cancellation marked as completed successfully");
      setCancelDialogOpen(false);
      await fetchBonds(0, 50);
      await fetchToBeCancelledBonds();
      calculateStatusCounts(bonds, selectedType);
      calculateActiveCounts(bonds);
    } catch (error) {
      console.error("Error marking cancellation as complete:", error);
      toast.error("Failed to mark cancellation as completed");
    } finally {
      setIsMarkingCancellationComplete(false);
    }
  };

  const handleRevertCancellation = async () => {
    if (!selectedBondToCancel) return;

    setIsMarkingCancellationComplete(true);
    try {
      const endpoint = getBondUpdateEndpoint(
        selectedBondToCancel.bond_type,
        selectedBondToCancel.bond_id
      );
      if (!endpoint) throw new Error("Invalid bond type");

      const payload = buildBondStatusPatchPayload(
        selectedBondToCancel.bond_type,
        "Approved"
      );

      await apiClient(endpoint, "PATCH", payload);
      toast.success("Bond cancellation reverted successfully");
      setCancelDialogOpen(false);
      await fetchBonds(0, 50);
      await fetchToBeCancelledBonds();
      calculateStatusCounts(bonds, selectedType);
      calculateActiveCounts(bonds);
    } catch (error) {
      console.error("Error reverting cancellation:", error);
      toast.error("Failed to revert cancellation");
    } finally {
      setIsMarkingCancellationComplete(false);
    }
  };

  return {
    selectedType,
    setselectedType,
    tabIndex,
    setTabIndex,
    loading,
    setLoading,
    isCancelling,
    setIsCancelling,
    searchQuery,
    setSearchQuery,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    bonds,
    setBonds,
    toBeCancelledBonds,
    setToBeCancelledBonds,
    statusCounts,
    setStatusCounts,
    activeCounts,
    setActiveCounts,
    start,
    setStart,
    end,
    setEnd,
    hasMore,
    setHasMore,
    cancelDialogOpen,
    setCancelDialogOpen,
    endorsementDialogOpen,
    setEndorsementDialogOpen,
    selectedBondToEndorse,
    setSelectedBondToEndorse,
    endorsementType,
    setEndorsementType,
    newBondAmount,
    setNewBondAmount,
    endorsementFile,
    setEndorsementFile,
    isSubmittingEndorsement,
    setIsSubmittingEndorsement,
    role,
    contactName,
    setContactName,
    contactEmail,
    setContactEmail,
    updatedName,
    setUpdatedName,
    counties,
    endorsementToCounty,
    setEndorsementToCounty,
    loadingCounties,
    estimatedAssets,
    setEstimatedAssets,
    dwbFirstName,
    setDwbFirstName,
    dwbMiddleName,
    setDwbMiddleName,
    dwbLastName,
    setDwbLastName,
    dwbSuffix,
    setDwbSuffix,
    dwbAlias,
    setDwbAlias,
    endorsementPrincipalId,
    setEndorsementPrincipalId,
    selectedBondToCancel,
    setSelectedBondToCancel,
    cancellationFile,
    setCancellationFile,
    isMarkingCancellationComplete,
    setIsMarkingCancellationComplete,
    dataCorrectionsList,
    setDataCorrectionsList,
    dataCorrectionsLoading,
    setDataCorrectionsLoading,
    selectedDataCorrection,
    setSelectedDataCorrection,
    dataCorrectionDetailOpen,
    setDataCorrectionDetailOpen,
    completingCorrectionId,
    setCompletingCorrectionId,
    stats,
    setStats,
    dataLoading,
    setDataLoading,
    statsOpen,
    setStatsOpen,
    refreshData,
    getCorrectionId,
    updateCorrectionStatusToComplete,
    calculateStatusCounts,
    calculateActiveCounts,
    fetchBonds,
    fetchToBeCancelledBonds,
    formatBondAmount,
    filteredData,
    currentRows,
    handleBondTypeClick,
    handleTabChange,
    handleChangePage,
    handleChangeRowsPerPage,
    formatTime,
    getEndorsementInstructions,
    getCancellationInstructionsList,
    handleMarkCancellationComplete,
    handleRevertCancellation,
  };
}
